import { ActionReducerMapBuilder, AsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { createTransform } from 'redux-persist';

import { actionKeyToLoaderKeyMap, actionMapForDataLoaders, actionMapForOtherLoaders } from 'constants/actions';
import { logoutUser } from './user';

import { ILoaderState } from '../state-mgmt/loader';
import { initialState } from './loader';
import { prettifyErrorResponse } from 'utils';

const getLoaderKey = ({
  actionKey,
  isDataLoader,
  languageCode,
  actionKeyAsLoaderKey,
  customLoadingKey,
}: {
  actionKey: string;
  isDataLoader: boolean;
  languageCode?: string;
  actionKeyAsLoaderKey?: boolean;
  customLoadingKey?: string;
}) => {
  if (customLoadingKey) {
    return customLoadingKey;
  }
  if (languageCode && !actionKeyAsLoaderKey) {
    return isDataLoader
      ? actionMapForDataLoaders[actionKeyToLoaderKeyMap?.[actionKey]?.[languageCode]]
      : actionMapForOtherLoaders[actionKeyToLoaderKeyMap?.[actionKey]?.[languageCode]];
  }
  return actionKey;
};

export const loadingCaseGenerator = (
  builder: ActionReducerMapBuilder<any>,
  arr: {
    thunkAction: AsyncThunk<any, any, any>;
    actionKey: string;
    isDataLoader?: boolean;
    actionKeyAsLoaderKey?: boolean;
  }[],
) => {
  let modifiedBuilder = builder;

  arr.forEach(({ thunkAction, actionKey, isDataLoader, actionKeyAsLoaderKey }) => {
    modifiedBuilder = modifiedBuilder
      .addCase(thunkAction.pending, (state: ILoaderState, action) => {
        if (isDataLoader) {
          state.dataLoaders[
            getLoaderKey({
              actionKey,
              isDataLoader,
              languageCode: action.meta.arg?.languageCode,
              actionKeyAsLoaderKey,
              customLoadingKey: action.meta.arg?.customLoadingKey,
            })
          ] = {
            ...(state.dataLoaders?.[
              getLoaderKey({
                actionKey,
                isDataLoader,
                languageCode: action.meta.arg?.languageCode,
                actionKeyAsLoaderKey,
                customLoadingKey: action.meta.arg?.customLoadingKey,
              })
            ] || {}),
            isLoading: true,
            hasErrors: false,
          };
        } else {
          state.otherLoaders[
            getLoaderKey({
              actionKey,
              isDataLoader,
              languageCode: action.meta.arg?.languageCode,
              actionKeyAsLoaderKey,
              customLoadingKey: action.meta.arg?.customLoadingKey,
            })
          ] = {
            ...(state.otherLoaders?.[
              getLoaderKey({
                actionKey,
                isDataLoader,
                languageCode: action.meta.arg?.languageCode,
                actionKeyAsLoaderKey,
                customLoadingKey: action.meta.arg?.customLoadingKey,
              })
            ] || {}),
            isLoading: true,
            hasErrors: false,
          };
        }
        return state;
      })
      .addCase(thunkAction.fulfilled, (state: ILoaderState, action) => {
        if (actionKey === actionMapForOtherLoaders.logoutUser) {
          state = initialState;
          return state;
        }
        if (isDataLoader) {
          state.dataLoaders[
            getLoaderKey({
              actionKey,
              isDataLoader,
              languageCode: action.meta.arg?.languageCode,
              actionKeyAsLoaderKey,
              customLoadingKey: action.meta.arg?.customLoadingKey,
            })
          ] = {
            ...(state.dataLoaders?.[
              getLoaderKey({
                actionKey,
                isDataLoader,
                languageCode: action.meta.arg?.languageCode,
                actionKeyAsLoaderKey,
                customLoadingKey: action.meta.arg?.customLoadingKey,
              })
            ] || {}),
            isLoading: false,
            hasErrors: false,
            errors: {},
          };
          if (state.isFetchingData) {
            state.total = state.total - 1;
            state.completed.push(
              getLoaderKey({
                actionKey,
                isDataLoader,
                languageCode: action.meta.arg?.languageCode,
                actionKeyAsLoaderKey,
                customLoadingKey: action.meta.arg?.customLoadingKey,
              }),
            );
          }
          if (!Object.values(state.dataLoaders || {}).some(loader => loader.isLoading) && state.isFetchingData) {
            state.isFetchingData = false;
            state.hasData = Object.values(state.dataLoaders || {}).some(loader => loader.hasErrors) ? false : true;
            state.updatedAt = Object.values(state.dataLoaders || {}).some(loader => loader.hasErrors)
              ? state.updatedAt
              : new Date().toISOString();
          }
          // if (state.total === 0) {
          //   state.hasData = true;
          //   state.updatedAt = new Date().toISOString();
          //   //! state.updatedBy = user && user.username ? user.username : ""; //! Do some work around for this
          // }
        } else {
          state.otherLoaders[
            getLoaderKey({
              actionKey,
              isDataLoader,
              languageCode: action.meta.arg?.languageCode,
              actionKeyAsLoaderKey,
              customLoadingKey: action.meta.arg?.customLoadingKey,
            })
          ] = {
            ...(state.otherLoaders?.[
              getLoaderKey({
                actionKey,
                isDataLoader,
                languageCode: action.meta.arg?.languageCode,
                actionKeyAsLoaderKey,
                customLoadingKey: action.meta.arg?.customLoadingKey,
              })
            ] || {}),
            isLoading: false,
            hasErrors: false,
            errors: {},
          };
        }
        if (action.payload?.showSuccess) {
          enqueueSnackbar(action.payload?.successMessage, {
            variant: 'success',
            autoHideDuration: 5000,
          });
        }
        return state;
      })
      .addCase(thunkAction.rejected, (state: ILoaderState, action: any) => {
        if (isDataLoader) {
          state.dataLoaders[
            getLoaderKey({
              actionKey,
              isDataLoader,
              languageCode: action.meta.arg?.languageCode,
              actionKeyAsLoaderKey,
              customLoadingKey: action.meta.arg?.customLoadingKey,
            })
          ] = {
            ...(state.dataLoaders?.[
              getLoaderKey({
                actionKey,
                isDataLoader,
                languageCode: action.meta.arg?.languageCode,
                actionKeyAsLoaderKey,
                customLoadingKey: action.meta.arg?.customLoadingKey,
              })
            ] || {}),
            isLoading: false,
            hasErrors: true,
            errors: action.payload,
          };

          if (!Object.values(state.dataLoaders || {}).some(loader => loader.isLoading) && state.isFetchingData) {
            state.isFetchingData = false;
            state.hasData = false;
          }
        } else {
          state.otherLoaders[
            getLoaderKey({
              actionKey,
              isDataLoader,
              languageCode: action.meta.arg?.languageCode,
              actionKeyAsLoaderKey,
              customLoadingKey: action.meta.arg?.customLoadingKey,
            })
          ] = {
            ...(state.otherLoaders?.[
              getLoaderKey({
                actionKey,
                isDataLoader,
                languageCode: action.meta.arg?.languageCode,
                actionKeyAsLoaderKey,
                customLoadingKey: action.meta.arg?.customLoadingKey,
              })
            ] || {}),
            isLoading: false,
            hasErrors: true,
            errors: action.payload,
          };
        }
        if (action.payload?.showError) {
          enqueueSnackbar(String(action.payload?.errorMessage || action.payload?.errorTitle || action.payload || ''), {
            variant: 'error',
            autoHideDuration: 5000,
          });
        }
        return state;
      });
  });

  return modifiedBuilder;
};

export const appWhitelistTransform = createTransform(
  (inboundState: any, key) => {
    // Only persist keys that are needed
    return {
      languageCode: inboundState.languageCode,
    };
  },
  (outboundState, key) => outboundState,
  { whitelist: ['app'] },
);
